import { supabase } from '@/integrations/supabase/client';

export interface ProgressionSuggestion {
  exerciseId: string;
  exerciseName: string;
  currentWeight: number;
  suggestedWeight: number;
  reason: string;
  confidence: 'high' | 'medium' | 'low';
  recentSets: RecentSet[];
}

export interface RecentSet {
  date: string;
  weight: number;
  reps: number;
  targetReps?: number;
}

interface SetLogWithExercise {
  weight: number;
  reps_completed: number;
  created_at: string;
  exercise_id: string;
  exercises: {
    name: string;
  } | null;
}

/**
 * Analyzes recent workout history for a client and generates
 * intelligent weight progression suggestions
 */
export async function getProgressionSuggestions(
  clientId: string,
  exerciseIds?: string[]
): Promise<ProgressionSuggestion[]> {
  const suggestions: ProgressionSuggestion[] = [];

  try {
    // Get workout logs for this client from the last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const { data: workoutLogs } = await supabase
      .from('workout_logs')
      .select('id')
      .eq('client_id', clientId)
      .gte('started_at', thirtyDaysAgo.toISOString());

    if (!workoutLogs || workoutLogs.length === 0) {
      return [];
    }

    const workoutLogIds = workoutLogs.map(wl => wl.id);

    // Build query for set logs
    let query = supabase
      .from('set_logs')
      .select(`
        weight,
        reps_completed,
        created_at,
        exercise_id,
        exercises:exercise_id (name)
      `)
      .in('workout_log_id', workoutLogIds)
      .not('weight', 'is', null)
      .order('created_at', { ascending: false });

    if (exerciseIds && exerciseIds.length > 0) {
      query = query.in('exercise_id', exerciseIds);
    }

    const { data: setLogs } = await query;

    if (!setLogs || setLogs.length === 0) {
      return [];
    }

    // Group sets by exercise
    const exerciseGroups = new Map<string, SetLogWithExercise[]>();
    
    for (const set of setLogs as SetLogWithExercise[]) {
      if (!set.weight || !set.reps_completed) continue;
      
      const existing = exerciseGroups.get(set.exercise_id) || [];
      existing.push(set);
      exerciseGroups.set(set.exercise_id, existing);
    }

    // Analyze each exercise for progression opportunities
    for (const [exerciseId, sets] of exerciseGroups) {
      const suggestion = analyzeExerciseProgression(exerciseId, sets);
      if (suggestion) {
        suggestions.push(suggestion);
      }
    }

    // Sort by confidence (high first)
    return suggestions.sort((a, b) => {
      const order = { high: 0, medium: 1, low: 2 };
      return order[a.confidence] - order[b.confidence];
    });
  } catch (error) {
    console.error('Error getting progression suggestions:', error);
    return [];
  }
}

function analyzeExerciseProgression(
  exerciseId: string,
  sets: SetLogWithExercise[]
): ProgressionSuggestion | null {
  if (sets.length < 3) return null; // Need at least 3 sets of history

  const exerciseName = sets[0].exercises?.name || 'Unknown Exercise';
  
  // Get the most recent weight used
  const recentSets = sets.slice(0, 9); // Last ~3 sessions worth
  const currentWeight = Math.max(...recentSets.map(s => s.weight));
  
  // Group by weight to analyze performance at each weight
  const performanceByWeight = new Map<number, number[]>();
  
  for (const set of recentSets) {
    const reps = performanceByWeight.get(set.weight) || [];
    reps.push(set.reps_completed);
    performanceByWeight.set(set.weight, reps);
  }

  // Check if they're consistently hitting high reps at current weight
  const repsAtCurrentWeight = performanceByWeight.get(currentWeight) || [];
  
  if (repsAtCurrentWeight.length < 3) return null;

  const avgReps = repsAtCurrentWeight.reduce((a, b) => a + b, 0) / repsAtCurrentWeight.length;
  const allHitTarget = repsAtCurrentWeight.every(r => r >= 10); // Assume 10+ reps = ready to progress
  const consistentPerformance = repsAtCurrentWeight.every(r => r >= avgReps - 1);

  // Calculate suggested progression
  let suggestedWeight = currentWeight;
  let reason = '';
  let confidence: 'high' | 'medium' | 'low' = 'low';

  if (allHitTarget && consistentPerformance && repsAtCurrentWeight.length >= 3) {
    // Strong case for progression
    suggestedWeight = calculateNextWeight(currentWeight);
    reason = `Consistently hitting ${Math.round(avgReps)} reps across ${repsAtCurrentWeight.length} sets. Ready to progress!`;
    confidence = 'high';
  } else if (avgReps >= 12 && repsAtCurrentWeight.length >= 2) {
    // Good case for progression
    suggestedWeight = calculateNextWeight(currentWeight);
    reason = `Averaging ${Math.round(avgReps)} reps - exceeding target range. Consider increasing weight.`;
    confidence = 'medium';
  } else if (avgReps >= 10 && consistentPerformance) {
    // Moderate case
    suggestedWeight = calculateNextWeight(currentWeight);
    reason = `Steady at ${Math.round(avgReps)} reps. Could try progressing next session.`;
    confidence = 'low';
  }

  if (suggestedWeight === currentWeight) return null;

  return {
    exerciseId,
    exerciseName,
    currentWeight,
    suggestedWeight,
    reason,
    confidence,
    recentSets: recentSets.slice(0, 6).map(s => ({
      date: s.created_at,
      weight: s.weight,
      reps: s.reps_completed,
    })),
  };
}

function calculateNextWeight(currentWeight: number): number {
  // Standard progression increments
  if (currentWeight < 20) {
    return currentWeight + 2.5; // Small increments for light weights
  } else if (currentWeight < 100) {
    return currentWeight + 5; // Standard 5lb jumps
  } else {
    return currentWeight + 10; // Larger jumps for heavy lifts
  }
}

/**
 * Check if a specific exercise is ready for progression
 */
export async function checkExerciseReadyForProgression(
  clientId: string,
  exerciseId: string
): Promise<ProgressionSuggestion | null> {
  const suggestions = await getProgressionSuggestions(clientId, [exerciseId]);
  return suggestions.length > 0 ? suggestions[0] : null;
}

/**
 * Get progression history for an exercise
 */
export async function getExerciseProgressionHistory(
  clientId: string,
  exerciseId: string,
  days: number = 90
): Promise<{ date: string; maxWeight: number; avgReps: number }[]> {
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - days);

  const { data: workoutLogs } = await supabase
    .from('workout_logs')
    .select('id, started_at')
    .eq('client_id', clientId)
    .gte('started_at', startDate.toISOString())
    .order('started_at', { ascending: true });

  if (!workoutLogs || workoutLogs.length === 0) return [];

  const workoutLogIds = workoutLogs.map(wl => wl.id);

  const { data: setLogs } = await supabase
    .from('set_logs')
    .select('workout_log_id, weight, reps_completed')
    .eq('exercise_id', exerciseId)
    .in('workout_log_id', workoutLogIds)
    .not('weight', 'is', null);

  if (!setLogs || setLogs.length === 0) return [];

  // Group by workout and calculate max weight / avg reps per session
  const workoutMap = new Map(workoutLogs.map(wl => [wl.id, wl.started_at]));
  const sessionData = new Map<string, { weights: number[]; reps: number[] }>();

  for (const set of setLogs) {
    const date = workoutMap.get(set.workout_log_id);
    if (!date || !set.weight || !set.reps_completed) continue;

    const dateKey = new Date(date).toISOString().split('T')[0];
    const existing = sessionData.get(dateKey) || { weights: [], reps: [] };
    existing.weights.push(set.weight);
    existing.reps.push(set.reps_completed);
    sessionData.set(dateKey, existing);
  }

  return Array.from(sessionData.entries()).map(([date, data]) => ({
    date,
    maxWeight: Math.max(...data.weights),
    avgReps: data.reps.reduce((a, b) => a + b, 0) / data.reps.length,
  }));
}
